import { lazy, Suspense, useEffect } from 'react'
import { Alert, Box, Flex, HStack, VStack, Text, Badge, Spinner } from '@chakra-ui/react';
import { Link as RouterLink, Outlet } from 'react-router-dom'
import { SyncProvider, useSync } from '../../providers/SyncProvider'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useGrantSession } from '../../hooks/useGrantSession'
import { PersonAvatar } from '../shared'
import { BackToOrganizerButton } from '../canvasser/BackToOrganizerButton'
import { useWalkSimEpoch } from '../../lib/walkSimBridge'
import { readWalkPrefs, sweepLegacyWalkKeys, writeWalkPrefs } from '../../lib/walkPrefs'

export function CanvasserLayout() {
  return (
    <SyncProvider>
      <CanvasserLayoutInner />
    </SyncProvider>
  )
}

function SyncStatusBadge() {
  const { isOnline, syncState, pendingCount, forceSync } = useSync()

  if (syncState === 'syncing') {
    return (
      <Badge bg="info.subtle" color="info.fg" variant="subtle" fontSize="xs">
        <HStack gap={1}>
          <Spinner size="xs" />
          <Text>Syncing...</Text>
        </HStack>
      </Badge>
    );
  }

  if (!isOnline) {
    return (
      <Badge colorPalette="orange" variant="subtle" fontSize="xs">Offline{pendingCount > 0 ? ` (${pendingCount})` : ''}
      </Badge>
    );
  }

  if (pendingCount > 0) {
    return (
      <Badge
        colorPalette="yellow"
        variant="subtle"
        fontSize="xs"
        cursor="pointer"
        onClick={forceSync}
      >
        {pendingCount} pending
              </Badge>
    );
  }

  if (syncState === 'error') {
    return (
      <Badge
        colorPalette="red"
        variant="subtle"
        fontSize="xs"
        cursor="pointer"
        onClick={forceSync}
      >Sync error
              </Badge>
    );
  }

  return (
    <Badge colorPalette="green" variant="subtle" fontSize="xs">Online
          </Badge>
  );
}

function RejectedVisitsBanner() {
  const { rejectedCount, rejectedSummaries } = useSync()
  if (rejectedCount === 0) return null
  return (
    <Alert.Root status="warning" borderRadius="none" px={4} py={2} fontSize="sm">
      <Alert.Indicator boxSize={4} />
      <VStack align="start" gap={0.5} minW={0}>
        <Text fontWeight="semibold" fontSize="sm">
          {rejectedCount} {rejectedCount === 1 ? 'visit' : 'visits'} couldn&apos;t be saved — show your organizer
        </Text>
        {rejectedSummaries.slice(0, 3).map(s => (
          <Text key={s} fontSize="xs" color="fg.muted" lineClamp={1}>{s}</Text>
        ))}
      </VStack>
    </Alert.Root>
  )
}

/** [Spec D4/§4.3] No bottom nav: Profile opens from the header avatar. */
function ProfileAvatarLink() {
  const { identity } = useGrantSession()
  const name = [identity?.subjectFirstName, identity?.subjectLastName].filter(Boolean).join(' ') || 'Canvasser'
  return (
    <RouterLink to="/canvasser/profile" aria-label="Your profile" style={{ minWidth: 44, minHeight: 44, display: 'grid', placeItems: 'center' }}>
      <PersonAvatar name={name} src={identity?.portraitUrl} size="sm" />
    </RouterLink>
  )
}

// DEV-only walk simulator: the ternary is statically false in production, so the chunk is dropped.
const WalkSimPanel = import.meta.env.DEV ? lazy(() => import('../../tools/walkSim/WalkSimPanel')) : null

function CanvasserLayoutInner() {
  // [Phase 1] The canvasser's one campaign comes from the grant — no picker, no ?campaignId=.
  const campaign = useGrantCampaign()
  // DEV: the sim bumps this on toggle so the current screen remounts and re-subscribes its GPS watch.
  const simEpoch = useWalkSimEpoch()

  // [Spec §8] Carry the pre-Phase-2 walk keys into canvasser_walk_prefs (readWalkPrefs folds them
  // in when no prefs exist yet), THEN sweep them — so a legacy "Try again" filter survives.
  useEffect(() => {
    writeWalkPrefs(readWalkPrefs())
    sweepLegacyWalkKeys()
  }, [])

  return (
    <Flex direction="column" h="100dvh" bg="bg.subtle">
      {/* Top Bar */}
      <Box
        bg="surface"
        borderBottom="1px"
        borderColor="border"
        px={4}
        py={3}
        position="sticky"
        top={0}
        zIndex={10}
      >
        <HStack justify="space-between">
          <HStack gap={2}>
            <Box
              w={7}
              h={7}
              bg="accent"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="accent.fg" fontWeight="bold" fontSize="sm">
                BT
              </Text>
            </Box>
            <VStack align="start" gap={0}>
              <Text fontWeight="bold" fontSize="md" lineHeight="short">
                {campaign?.label ?? 'Canvasser'}
              </Text>
              {campaign && (
                <Text fontSize="2xs" color="fg.muted" lineHeight="short">
                  Canvasser
                </Text>
              )}
            </VStack>
          </HStack>
          <HStack gap={2}>
            <BackToOrganizerButton />
            <SyncStatusBadge />
            <ProfileAvatarLink />
          </HStack>
        </HStack>
      </Box>

      <RejectedVisitsBanner />

      {WalkSimPanel && (
        <Suspense fallback={null}>
          <WalkSimPanel />
        </Suspense>
      )}

      {/* Main Content — a full-height flex child so the walk screen can fill it (h="100%"). */}
      <Box flex={1} minH={0} overflowY="auto" key={import.meta.env.DEV ? simEpoch : undefined}>
        <Outlet />
      </Box>
    </Flex>
  );
}
